import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ChatPad from '../ui/ChatPad';
import { LibrarianService } from '../../services/llmClient/LibrarianService';
import { saveLibrarianInteraction } from '../../services/supabaseClient/videoLibraryService';
import './LibrarianVideoAssistant.css';

const LibrarianVideoAssistant = ({ video, transcript, currentTime }) => {
  const [error, setError] = useState(null);

  const handleSendMessage = async ({ message }) => {
    setError(null);

    try {
      const response = await LibrarianService.chatWithVideoContext({
        message,
        transcript: transcript || '',
        currentTime,
        videoMetadata: video,
      });

      // Save interaction to database
      if (video?.id) {
        await saveLibrarianInteraction(video.id, {
          user_message: message,
          assistant_response: response.message,
          current_time: currentTime,
        });
      }

      return {
        response: response.message,
        suggestions: response.timestamp ? [`Jump to ${formatTime(response.timestamp)}`] : [],
      };
    } catch (err) {
      setError('Failed to get response from Librarian. Please try again.');
      throw err;
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  const welcomeMessage = `Hello! I'm here to help you understand this video${transcript ? ' using the transcript' : ''}. Feel free to ask me questions about the content, request summaries, or ask for explanations of specific concepts.`;

  const welcomeHints = transcript
    ? [
        'Can you summarize the main points of this video?',
        'What are the key concepts covered?',
        'Can you create quiz questions to test my understanding?',
        'Explain this concept in simpler terms',
      ]
    : ['Ask me about the video content', 'Request explanations of concepts'];

  return (
    <div className="librarian-video-assistant">
      {error && (
        <div className="librarian-error">
          <i className="fa-solid fa-exclamation-circle" />
          <span>{error}</span>
          <button onClick={() => setError(null)} aria-label="Dismiss error">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
      )}

      {!transcript && (
        <div className="librarian-warning">
          <i className="fa-solid fa-info-circle" />
          <span>No transcript available - responses may be limited</span>
        </div>
      )}

      <ChatPad
        agentName="Librarian"
        agentEmoji="📚"
        agentColor="tertiary"
        placeholder="Ask about the video..."
        welcomeMessage={welcomeMessage}
        welcomeHints={welcomeHints}
        onSendMessage={handleSendMessage}
      />
    </div>
  );
};

LibrarianVideoAssistant.propTypes = {
  video: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    ml_courses_staging: PropTypes.shape({
      title: PropTypes.string,
    }),
  }).isRequired,
  transcript: PropTypes.string,
  currentTime: PropTypes.number,
};

LibrarianVideoAssistant.defaultProps = {
  transcript: null,
  currentTime: 0,
};

export default LibrarianVideoAssistant;
